import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getIntent } from "@/lib/onboardingIntent";
import { resolvePostAuthRoute } from "@/lib/postAuthRoute";
import { hasDraftListing } from "@/lib/draftListing";
import { flushPendingTerms } from "@/lib/termsAcceptance";
import { flushPendingConsents } from "@/lib/consent";
import { reportOAuthReturn } from "@/lib/oauth";

interface AuthContext {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthContext>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

/**
 * The user object screens see changes only when it is a different person
 * (or their account details were updated). Supabase hands out a new session
 * on every token refresh and every time the tab regains focus; passing those
 * through made every `[user]` effect re-run, so the admin reloaded all its
 * data, flashed its spinners and dropped work in progress on each tab switch.
 */
export const keepUser = (prev: User | null, next: User | null, event?: string): User | null => {
  if (!next) return null;
  if (prev && prev.id === next.id && event !== "USER_UPDATED") return prev;
  return next;
};

// This device only (Oct 28): a global sign-out revokes every session of the
// account, including the one a founder's ChatGPT holds through the MCP
// server, and the connector then fails with "refresh token not found" until
// it is connected again. Deleting a profile (Profile.tsx) still signs out
// everywhere on purpose.
const signOut = async () => {
  await supabase.auth.signOut({ scope: "local" });
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let previousUserId: string | null = null;
    let initialized = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser((prev) => keepUser(prev, session?.user ?? null, event));
      setLoading(false);

      // Stamp Terms/Privacy acceptance on first auth through our UI, no-op
      // unless the pending flag was set just before signing in. Also flush any
      // marketing/photo consents stashed before the (possibly OAuth) sign-in.
      if (session?.user) {
        flushPendingTerms(session.user.id);
        flushPendingConsents(session.user.id);
        reportOAuthReturn();
      }

      // Only react to actual sign-in transitions, not token refreshes,
      // tab-focus revalidations, or initial session restoration. Otherwise
      // navigating to /profile would race with a forced redirect to /onboarding.
      const newUserId = session?.user?.id ?? null;
      const isFreshSignIn =
        event === "SIGNED_IN" && initialized && newUserId !== previousUserId;
      previousUserId = newUserId;
      initialized = true;

      if (!isFreshSignIn || !session?.user) return;

      // Defer the Supabase call so it doesn't block the auth callback
      setTimeout(async () => {
        const { data } = await supabase
          .from("profiles")
          .select("user_types")
          .eq("user_id", session.user.id)
          .single();

        const types = (data?.user_types as string[]) ?? [];
        const path = window.location.pathname;
        // Pages that own their own post-auth transition, don't yank the
        // user out from under them.
        if (path === "/welcome" || path === "/login" || path === "/onboarding" || path === "/owner-onboarding") return;
        if (path === "/list-property/new") return;

        const intent = getIntent();
        const draft = hasDraftListing();

        // Only intervene when the user clearly lacks a role and has no
        // intent/draft to disambiguate. Otherwise let them stay where they
        // are, they may have landed on /properties intentionally.
        if (types.length === 0 && !intent && !draft) {
          if (path !== "/" && path !== "/onboarding") {
            window.location.href = "/onboarding";
          }
          return;
        }

        // If they're sitting on the homepage or login, route them onward.
        if (path === "/") {
          window.location.href = resolvePostAuthRoute({ types, intent, hasDraft: draft });
        }
      }, 500);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      previousUserId = session?.user?.id ?? null;
      initialized = true;
      setSession(session);
      setUser((prev) => keepUser(prev, session?.user ?? null));
      setLoading(false);
      if (session?.user) {
        flushPendingTerms(session.user.id);
        flushPendingConsents(session.user.id);
        reportOAuthReturn();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = useMemo(() => ({ user, session, loading, signOut }), [user, session, loading]);
  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
